import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Loader2, Send, CheckCircle2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sizeLabels = { premium: 'Premium (+15m²)', standard: 'Standard (9-12m²)', compact: 'Compact (3-6m²)' };
const statusColors = { available: '#10B981', pending: '#F59E0B', reserved: '#EF4444' };
const statusLabels = { available: 'Disponible', pending: 'En attente', reserved: 'Réservé' };

const StandReservation = () => {
  const [stands, setStands] = useState([]);
  const [selectedStand, setSelectedStand] = useState(null);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ company_name: '', contact_name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stands`).then(r => setStands(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const zones = [...new Set(stands.map(s => s.zone))].sort();
  const filtered = zoneFilter === 'all' ? stands : stands.filter(s => s.zone === zoneFilter);
  const stats = { total: stands.length, available: stands.filter(s => s.status === 'available').length, pending: stands.filter(s => s.status === 'pending').length, reserved: stands.filter(s => s.status === 'reserved').length };

  const handleReserve = async (e) => {
    e.preventDefault();
    if (!selectedStand) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/stand-reservations`, { ...formData, stand_id: selectedStand.id });
      toast.success('Demande de réservation envoyée !');
      setSuccess(true);
      setStands(prev => prev.map(s => s.id === selectedStand.id ? { ...s, status: 'pending' } : s));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réservation');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-20">
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="stands-hero">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" />
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Exposition</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Plan des Stands</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed mb-8">Sélectionnez un stand disponible et réservez votre emplacement pour le Forum URBANIK 2026.</p>
          <div className="flex items-center gap-6 flex-wrap">
            {Object.entries(statusLabels).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: statusColors[k] }} />
                <span className="text-sm text-white/70">{v} ({stats[k] || 0})</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20" data-testid="stands-grid">
        <div className="container-institutional">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Grid */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm font-medium text-[#4A5568]">Zone :</span>
                {['all', ...zones].map(z => (
                  <button key={z} onClick={() => setZoneFilter(z)} className={`px-4 py-1.5 text-sm rounded-full transition-colors ${zoneFilter === z ? 'bg-[#1E3A5F] text-white' : 'bg-[#F7F9FC] text-[#4A5568] hover:bg-[#E1E8F0]'}`} data-testid={`zone-filter-${z}`}>
                    {z === 'all' ? 'Toutes' : `Zone ${z}`}
                  </button>
                ))}
              </div>
              {loading ? <p className="text-[#94A3B8] py-12 text-center">Chargement...</p> : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {filtered.map(stand => {
                    // Resolve stand card visual state — replaces nested ternary.
                    let stateClass;
                    if (selectedStand?.id === stand.id) {
                      stateClass = 'border-[#F5A623] bg-[#F5A623]/5 shadow-md';
                    } else if (stand.status === 'available') {
                      stateClass = 'border-[#E1E8F0] bg-white hover:border-[#1E3A5F] cursor-pointer';
                    } else {
                      stateClass = 'border-[#E1E8F0] bg-[#F7F9FC] opacity-60 cursor-not-allowed';
                    }
                    return (
                      <button key={stand.id} onClick={() => stand.status === 'available' && setSelectedStand(stand)} disabled={stand.status !== 'available'}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${stateClass}`}
                        data-testid={`stand-${stand.stand_number}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-[#1E3A5F]">{stand.stand_number}</span>
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColors[stand.status] }} />
                        </div>
                        <p className="text-xs text-[#6B7C93]">{sizeLabels[stand.size] || stand.size}</p>
                        <p className="text-sm font-semibold text-[#1E3A5F] mt-1">{stand.price?.toLocaleString('fr-FR')} FCFA</p>
                        {stand.exhibitor && <p className="text-xs text-[#F5A623] mt-1 truncate">{stand.exhibitor.company_name}</p>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reservation Panel */}
            <div className="w-full lg:w-96 lg:sticky lg:top-24 lg:self-start">
              {success ? (
                <div className="bg-white border border-[#E1E8F0] rounded-lg p-8 text-center" data-testid="reservation-success">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">Demande envoyée</h3>
                  <p className="text-sm text-[#6B7C93]">Votre demande de réservation a été envoyée. Nous vous contacterons sous 48h.</p>
                  <button onClick={() => { setSuccess(false); setSelectedStand(null); setFormData({ company_name: '', contact_name: '', email: '', phone: '', message: '' }); }} className="mt-4 text-sm text-[#1E3A5F] hover:text-[#F5A623] transition-colors">Nouvelle réservation</button>
                </div>
              ) : selectedStand ? (
                <div className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden" data-testid="reservation-form">
                  <div className="p-5 bg-[#F7F9FC] border-b border-[#E1E8F0]">
                    <h3 className="font-bold text-[#1E3A5F]">Réserver le stand {selectedStand.stand_number}</h3>
                    <p className="text-sm text-[#6B7C93] mt-1">Zone {selectedStand.zone} – {sizeLabels[selectedStand.size]} – {selectedStand.price?.toLocaleString('fr-FR')} FCFA</p>
                  </div>
                  <form onSubmit={handleReserve} className="p-5 space-y-4">
                    {[
                      { name: 'company_name', label: 'Entreprise', required: true },
                      { name: 'contact_name', label: 'Nom du contact', required: true },
                      { name: 'email', label: 'Email', type: 'email', required: true },
                      { name: 'phone', label: 'Téléphone', required: true },
                    ].map(f => (
                      <div key={f.name}>
                        <label className="form-label">{f.label} {f.required && <span className="text-[#F5A623]">*</span>}</label>
                        <input name={f.name} type={f.type || 'text'} value={formData[f.name]} onChange={e => setFormData(p => ({ ...p, [f.name]: e.target.value }))} required={f.required} className="form-input" data-testid={`reserve-${f.name}`} />
                      </div>
                    ))}
                    <div>
                      <label className="form-label">Message</label>
                      <textarea name="message" value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} className="form-input min-h-[70px] resize-none" data-testid="reserve-message" />
                    </div>
                    <button type="submit" disabled={submitting} className="form-submit-btn w-full" data-testid="reserve-submit">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</> : <><Send className="w-4 h-4 mr-2" />Réserver ce stand</>}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-white border border-[#E1E8F0] rounded-lg p-8 text-center" data-testid="no-stand-selected">
                  <MapPin className="w-10 h-10 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-sm text-[#6B7C93]">Sélectionnez un stand disponible pour commencer la réservation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StandReservation;
